import { useState, useEffect } from 'react';
import { Button } from './Button';

export interface BuyInModalProps {
  isOpen: boolean;
  balance: number;
  minBuyIn: number;
  maxBuyIn: number;
  stakes: string;
  onConfirm: (amount: number) => void;
  onClose: () => void;
}

export function BuyInModal({
  isOpen,
  balance,
  minBuyIn,
  maxBuyIn,
  stakes,
  onConfirm,
  onClose,
}: BuyInModalProps) {
  const effectiveMax = Math.min(maxBuyIn, balance);
  const [amount, setAmount] = useState(0);
  const [inputValue, setInputValue] = useState('0.00');

  useEffect(() => {
    if (isOpen) {
      const next = Math.min(effectiveMax, Math.max(minBuyIn, balance));
      setAmount(next);
      setInputValue((next / 100).toFixed(2));
    }
  }, [isOpen, minBuyIn, effectiveMax, balance]);

  if (!isOpen) return null;

  const syncFromInput = (raw: string) => {
    setInputValue(raw);
    const parsed = parseFloat(raw);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      setAmount(Math.round(parsed * 100));
    }
  };

  const syncFromAmount = (cents: number) => {
    const clamped = Math.min(effectiveMax, Math.max(minBuyIn, cents));
    setAmount(clamped);
    setInputValue((clamped / 100).toFixed(2));
  };

  const quickAmounts = [
    minBuyIn,
    Math.round((minBuyIn + effectiveMax) / 2),
    effectiveMax,
  ].filter((a, i, arr) => arr.indexOf(a) === i);

  const handleConfirm = () => {
    const parsed = parseFloat(inputValue);
    const val = Number.isFinite(parsed) ? Math.round(parsed * 100) : amount;
    const clamped = Math.min(effectiveMax, Math.max(minBuyIn, val));
    if (clamped >= minBuyIn && clamped <= effectiveMax) {
      onConfirm(clamped);
      onClose();
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-surface border border-white/10 text-text text-lg font-semibold text-right ' +
    'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-surface border border-white/10 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text">{stakes} NLH</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="flex items-center justify-between mb-4">
          <span className="text-text-muted">Balance</span>
          <span className="text-primary font-semibold tabular-nums">
            ${(balance / 100).toFixed(2)}
          </span>
        </div>

        <div className="mb-4">
          <label className="block text-text-muted text-sm mb-2">Enter Amount ($)</label>
          <input
            type="text"
            inputMode="decimal"
            value={inputValue}
            onChange={(e) => syncFromInput(e.target.value)}
            placeholder="0.00"
            className={inputClass}
          />
        </div>

        <div className="mb-4">
          <input
            type="range"
            min={minBuyIn}
            max={effectiveMax}
            value={amount}
            onChange={(e) => syncFromAmount(Number(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none bg-surface-elevated accent-primary"
          />
          <div className="flex justify-between text-text-muted text-sm mt-1">
            <span>Min ${(minBuyIn / 100).toFixed(2)}</span>
            <span>Max ${(effectiveMax / 100).toFixed(2)}</span>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          {quickAmounts.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => syncFromAmount(a)}
              className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${
                amount === a
                  ? 'bg-primary text-white'
                  : 'bg-surface-elevated text-text-muted hover:bg-white/10 hover:text-text'
              }`}
            >
              ${(a / 100).toFixed(2)}
            </button>
          ))}
        </div>

        <Button variant="primary" size="lg" className="w-full" onClick={handleConfirm}>
          Buy-In
        </Button>

        <p className="mt-3 text-text-subtle text-xs">
          You can change your default buy-in from the settings menu.
        </p>
      </div>
    </div>
  );
}
